import {
  Tooltip,
  Chip,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from "@mui/material";

import DsButton from "@accredo/fe-shared/theme/components/Button.jsx";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import React, { useState } from "react";

const BookingStatus = ({
  booking,
  showLabel = false,
  onChange,
  onComplete,
  isLoading = true,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [completeConfirmOpen, setCompleteConfirmOpen] = useState(false);

  const is_assignment = booking?.category === "assignment";

  const handleStatusChange = (status) => {
    setSelectedStatus(status);
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (onChange) {
      await onChange(selectedStatus, booking);
      setConfirmOpen(false);
      handleMenuClose();
    } else {
      setConfirmOpen(false);
      handleMenuClose();
    }
  };

  const handleCompleteConfirm = async () => {
    if (onComplete) {
      await onComplete(booking);
    }
    setCompleteConfirmOpen(false);
  };

  const statusMap = [
    {
      check: (b) => b.status === "pending",
      label: "O",
      color: "warning",
      tooltip: "Offen",
      text: "Offen",
      key: "pending",
      disabled: booking?.status !== "released",
    },
    {
      check: (b) => b.status === "released",
      label: "F",
      color: "info",
      tooltip: "Freigegeben",
      text: "Freigegeben",
      key: "released",
      disabled: booking?.status !== "pending",
    },
    {
      check: (b) => b.status === "exported",
      label: "E",
      color: "secondary",
      tooltip: "Exportiert",
      text: "Exportiert",
      key: "exported",
      disabled: true,
    },
    {
      check: (b) => b.status === "finished",
      label: "A",
      color: "primary",
      tooltip: "Abgeschlossen",
      text: "Abgeschlossen",
      key: "finished",
      disabled: true,
    },
  ];

  const status = statusMap.find((s) => s.check(booking));
  const [anchorEl, setAnchorEl] = useState(null);

  if (!status) return null;

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const canComplete =
    is_assignment && booking?.status === "pending" && !!onComplete;

  return (
    <>
      <Box display="inline-flex" alignItems="center" gap={1}>
        <Tooltip title={status.tooltip}>
          <Chip label={status.label} color={status.color} size="small" />
        </Tooltip>
        {showLabel && <Typography variant="body2">{status.text}</Typography>}
        {canComplete && (
          <Tooltip title="Abtretung abschließen (erstellt automatisch eine Gegenbuchung)">
            <IconButton
              size="small"
              onClick={() => setCompleteConfirmOpen(true)}
              sx={{ p: 0.5 }}
              aria-label="Abtretung abschließen"
            >
              <KeyboardArrowDownIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {!is_assignment && onChange && (
          <>
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              sx={{ p: 0.5 }}
              aria-label="Status ändern"
            >
              <KeyboardArrowDownIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              {statusMap.map((s) => (
                <MenuItem
                  key={s.key}
                  selected={s.key === status.key}
                  onClick={() => handleStatusChange(s)}
                  disabled={s.disabled}
                >
                  <Chip
                    label={s.label}
                    color={s.color}
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  {s.text}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </Box>
      <ConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
        title="Status ändern"
        message={`Möchten Sie den Status wirklich auf "${selectedStatus?.text}" ändern?`}
        isLoading={isLoading}
      />
      <ConfirmModal
        open={completeConfirmOpen}
        onClose={() => setCompleteConfirmOpen(false)}
        onConfirm={handleCompleteConfirm}
        title="Abtretung abschließen"
        message="Wurde die Abtretungszahlung durch den Versicherer beglichen? Diese Aktion schließt die Buchung ab und erstellt automatisch eine statistische Gegenbuchung."
        isLoading={isLoading}
      />
    </>
  );
};

const ConfirmModal = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  isLoading,
}) => {
  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>{title || "Bestätigung"}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {message || "Möchten Sie diese Aktion wirklich ausführen?"}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <DsButton onClick={onClose} variant="tertiary">
          Abbrechen
        </DsButton>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="primary"
          disabled={isLoading}
          loading={isLoading}
        >
          Bestätigen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BookingStatus;
